import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectionsSliceActions } from "../store/slices/selectionsSlice";
import classes from "./RightSidebar.module.css";

const normalizeName = (value) =>
    String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

const DEFAULT_MOVEMENT_ROWS = 5;

const formatNumber = (value) => {
    const numericValue = Number(String(value ?? "").replace(/,/g, ""));
    if (!Number.isFinite(numericValue)) return "";
    return new Intl.NumberFormat("en-US").format(numericValue);
};

const toNumber = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const parsed = Number(String(value ?? "").replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
};

const getRound = (row) => {
    const value = row?.round_number ?? row?.round ?? row?.Round ?? row?.rounds;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : value;
};

const getAdmin1Name = (row) =>
    row?.admin_1_name ||
    row?.admin1_name ||
    row?.admin_1 ||
    row?.admin1 ||
    row?.admin_1_name_en;

const getAdmin1Pcode = (row) => row?.admin_1_pcode || row?.admin1_pcode || "";

const getOriginName = (row) =>
    row?.idp_origin_admin_1 ||
    row?.origin_admin_1_name ||
    row?.origin_admin1_name ||
    row?.admin_1_origin_name ||
    row?.admin1_origin_name ||
    row?.origin_admin_1 ||
    row?.origin_admin1 ||
    row?.origin_name ||
    row?.origin;

const getOriginPcode = (row) =>
    row?.idp_origin_admin_1_pcode ||
    row?.origin_admin_1_pcode ||
    row?.origin_admin1_pcode ||
    row?.admin_1_origin_pcode ||
    row?.admin1_origin_pcode ||
    row?.origin_pcode ||
    "";

const matchesSelectedCause = (value, selectedCause) => {
    if (!selectedCause || selectedCause === "All" || selectedCause === "None") {
        return true;
    }
    return String(value || "")
        .toLowerCase()
        .includes(String(selectedCause).toLowerCase());
};

const matchesAdmin1 = (row, movementAdmin1, side) => {
    const selectedPcode = movementAdmin1?.admin1_pcode;
    const selectedName = normalizeName(movementAdmin1?.name);
    const pcode = side === "origin" ? getOriginPcode(row) : getAdmin1Pcode(row);
    const name = side === "origin" ? getOriginName(row) : getAdmin1Name(row);

    return (
        (selectedPcode && pcode && String(pcode) === String(selectedPcode)) ||
        (selectedName && normalizeName(name) === selectedName)
    );
};

const IDPMovement = () => {
    const dispatch = useDispatch();
    const [showAllMovement, setShowAllMovement] = useState(false);
    const isMovementActive = useSelector((state) => state.selection.idpMovement);
    const movementAdmin1 = useSelector((state) => state.selection.idpMovementAdmin1);
    const movementDirection = useSelector(
        (state) => state.selection.idpMovementDirection || "arrivals",
    );
    const selectedYear = useSelector((state) => state.selection.year);
    const selectedCause = useSelector((state) => state.selection.cause);
    const selectedOperation = useSelector((state) => state.selection.operation);
    const admin1Rows = useSelector((state) => state.agg.admin1 || []);

    const movementRows = useMemo(() => {
        if (!isMovementActive || !movementAdmin1) return [];

        const selectedYearNumber = Number(selectedYear);
        const hasSelectedYear = Number.isFinite(selectedYearNumber);
        const selectedRound = getRound(movementAdmin1);
        const hasSelectedRound = selectedRound !== "" && selectedRound !== null && selectedRound !== undefined;
        const effectiveOperation =
            selectedOperation && selectedOperation !== "All" ? selectedOperation : "";
        const grouped = new Map();

        admin1Rows.forEach((row) => {
            if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return;
            if (hasSelectedRound && getRound(row) !== selectedRound) return;
            if (!matchesSelectedCause(row.reason_for_displacement, selectedCause)) return;
            const operation = row.operation || row.Operation || row.operation_name;
            if (effectiveOperation && operation !== effectiveOperation) return;

            const isDeparting = movementDirection === "departing";
            const selectedSide = isDeparting ? "origin" : "destination";
            if (!matchesAdmin1(row, movementAdmin1, selectedSide)) return;

            const label = isDeparting ? getAdmin1Name(row) : getOriginName(row);
            const key = isDeparting ? getAdmin1Pcode(row) || label : getOriginPcode(row) || label;
            if (!label || normalizeName(label) === "not available") return;
            const isInternal = matchesAdmin1(row, movementAdmin1, isDeparting ? "destination" : "origin");
            const existing = grouped.get(key);

            grouped.set(key, {
                label,
                isInternal: Boolean(existing?.isInternal || isInternal),
                value: (existing?.value || 0) + toNumber(row.idps ?? row.total_idps ?? row.value),
            });
        });

        return Array.from(grouped.values())
            .filter((row) => row.value > 0)
            .sort((a, b) => b.value - a.value);
    }, [
        admin1Rows,
        isMovementActive,
        movementAdmin1,
        movementDirection,
        selectedCause,
        selectedOperation,
        selectedYear,
    ]);

    useEffect(() => {
        setShowAllMovement(false);
    }, [movementAdmin1, movementDirection]);

    if (!isMovementActive || !movementAdmin1) return null;

    const heading = movementDirection === "departing" ? "Admin 1 Destination" : "Admin 1 Origin";
    const visibleMovementRows = showAllMovement
        ? movementRows
        : movementRows.slice(0, DEFAULT_MOVEMENT_ROWS);
    const hasMoreMovementRows = movementRows.length > DEFAULT_MOVEMENT_ROWS;
    const selectMovementDirection = (direction) => {
        dispatch(selectionsSliceActions.setIdpMovementDirection(direction));
    };

    return (
        <div className={classes.box_other}>
            <div className={classes.idps_movement_box}>
                <p className={classes.idps_box_title}>IDPs Movement</p>
                <div className={classes.idps_section_separator} />
                <div className={classes.idps_inner_box}>
                    <div className={classes.idps_movement_summary}>
                        <div>
                            <p className={classes.idps_iso_code}>
                                {movementAdmin1.admin1_pcode || "Admin 1"}
                            </p>
                            <p className={classes.idps_movement_location}>{movementAdmin1.name}</p>
                        </div>
                        <div><p className={classes.idps_box_figure}>
                                {formatNumber(movementAdmin1.value)}
                            </p>
                            <p className={classes.idps_observed_label}>IDPs Observed</p>
                            
                        </div>
                    </div>
                </div>

                <div className={classes.idps_flow_connector}>
                    <span className={classes.idps_flow_dot}></span>
                    <div className={classes.idps_flow_card}>
                        <span className={classes.idps_flow_arrow_up}></span>
                        <span className={classes.idps_flow_arrow_down}></span>
                        <button
                            type="button"
                            className={`${classes.idps_flow_in} ${
                                movementDirection === "arrivals"
                                    ? classes.idps_flow_in_active
                                    : ""
                            }`}
                            aria-pressed={movementDirection === "arrivals"}
                            onClick={() => selectMovementDirection("arrivals")}
                        >
                            <span className={classes.triangle}></span> ARRIVALS
                        </button>
                        <button
                            type="button"
                            className={`${classes.idps_flow_out} ${
                                movementDirection === "departing"
                                    ? classes.idps_flow_out_active
                                    : ""
                            }`}
                            aria-pressed={movementDirection === "departing"}
                            onClick={() => selectMovementDirection("departing")}
                        >
                            <span className={classes.triangle_down}></span> DEPARTING
                        </button>
                    </div>
                    <span className={`${classes.idps_flow_dot} ${classes.idps_flow_dot_blue}`}></span>
                </div>

                <div className={classes.idps_inner_box_two}>
                    {movementRows.length ? (
                        <>
                            <div className={classes.idps_inner_box_two_inner_heading}>
                                <p>{heading}</p>
                                <p className={classes.idps_inner_box_two_inner_heading_right}>No. IDPs</p>
                            </div>
                            <div
                                className={`${classes.idps_movement_rows}${
                                    showAllMovement
                                        ? ` ${classes.idps_movement_rows_scroll}`
                                        : ""
                                }`}
                            >
                                {visibleMovementRows.map((row) => (
                                    <div
                                        className={classes.idps_inner_box_two_inner_body}
                                        key={row.label}
                                    >
                                        <p className={classes.idps_movement_row_label}>
                                            <span>{row.label}</span>
                                            {row.isInternal && (
                                                <span
                                                    className={`${classes.idps_internal_movement_icon} ${
                                                        movementDirection === "departing"
                                                            ? classes.idps_internal_movement_icon_departing
                                                            : classes.idps_internal_movement_icon_arrivals
                                                    }`}
                                                    title="Internal IDP movement with in Admin 1"
                                                    aria-label="Internal IDP movement with in Admin 1"
                                                >
                                                    &#8635;
                                                </span>
                                            )}
                                        </p>
                                        <p>{formatNumber(row.value)}</p>
                                    </div>
                                ))}
                            </div>
                            {hasMoreMovementRows && !showAllMovement && (
                                <button
                                    type="button"
                                    className={classes.idps_movement_see_all}
                                    onClick={() => setShowAllMovement(true)}
                                >
                                    See all movement
                                </button>
                            )}
                        </>
                    ) : (
                        <div className={classes.idps_movement_empty}>
                            <span
                                className={classes.idps_movement_empty_icon}
                                aria-hidden="true"
                            >
                                !
                            </span>
                            <p>No movement data available</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IDPMovement;
